import React from "react";
import { Routes, Route } from "react-router-dom";
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import Login from "./pages/Login";
import Register from "./pages/Register";
import DefaultLayout from "./layouts/DefaultLayout";
import ProtectedLayout from "./layouts/ProtectedLayout";
import ProtectedDriverLayout from "./layouts/ProtectedDriverLayout";
import NotificationBar from "./components/notification/NotificationBar";
import LanguageSelector from "./components/LanguageSelector";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import SelectionPage from "./pages/SelectionPage";
import Pickup from "./pages/Pickup";
import DropOff from "./pages/Dropoff";
import MyInfo from "./pages/MyInfo";
import {CustomerOrdersView} from "./pages/CustomerHistory";
import OrdersPage from './pages/OrdersPage';
import DriverLogin from './pages/DriverLogin';
import {DriverOrdersView} from './pages/DriverOrders';
import { DriverHistoryView } from "./pages/DriverHistory";
import CurrentOrder from "./pages/CurrentOrder";
import CustomerOrderList from "./pages/CustomerOrderList";
import ForgotPassword from "./pages/ForgotPassword";
import DriverSelectionPage from "./pages/DriverSelectionPage";
import OrderSearch from './pages/OrderSearch';
import EditPickup from './pages/EditPickup';
import EditDropoff from './pages/EditDropoff';
import ResetPassword from "./pages/ResetPassword";
import DriverInfo from "./pages/DriverInfo";
import DriverChangePassword from "./pages/DriverPassword";
import ChangePassword from "./pages/ChangePassword";
import OrderSuccessCard from "./components/OrderSuccessCard";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import AccountDeleted from "./pages/AccountDeleted";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "./hooks/redux-hooks";
import { useTranslation } from "react-i18next";
import { clearSession, logout } from "./slices/authSlice";

function stringAvatar(name?: string) {
  const firstChar = (name || "").trim().charAt(0).toUpperCase() || "?";
  return {
    children: firstChar,
    sx: {
      width: 36,
      height: 36,
      fontSize: 16,
      bgcolor: "primary.main",
    },
  };
}

function App() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const basicUserInfo = useAppSelector((state) => state.auth.basicUserInfo);
  const driverInfo = useAppSelector((state) => state.auth.driverInfo);
  const isDriver = localStorage.getItem("isDriver") === "true" || !!driverInfo;
  const userInfo = isDriver ? driverInfo : basicUserInfo;
  const userName = userInfo?.name || "";
  const userEmail = userInfo?.email || "";
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(
    null
  );
  const isMenuOpen = Boolean(menuAnchorEl);

  React.useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.storageArea === localStorage && event.key === "userInfo" && event.newValue === null && basicUserInfo) {
        dispatch(clearSession());
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [basicUserInfo, dispatch]);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleManageAccount = () => {
    navigate(isDriver ? "/driver-info" : "/myinfo");
    handleCloseMenu();
  };

  const handleChangePassword = () => {
    navigate(isDriver ? "/driver-password" : "/change-password");
    handleCloseMenu();
  };

  const handleLogout = () => {
    dispatch(logout());
    handleCloseMenu();
    navigate(isDriver ? "/driver-login" : "/login");
  };

  return (
    <>
      <I18nextProvider i18n={i18n}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          pt: 1,
        }}
      >
        <LanguageSelector />
        {userName ? (
          <>
            <IconButton
              onClick={handleOpenMenu}
              size="small"
              sx={{ p: 0 }}
              aria-controls={isMenuOpen ? "user-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={isMenuOpen ? "true" : undefined}
            >
              <Avatar {...stringAvatar(userName)} />
            </IconButton>
            <Menu
              id="user-menu"
              anchorEl={menuAnchorEl}
              open={isMenuOpen}
              onClose={handleCloseMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <Box sx={{ px: 2, py: 1.5, minWidth: 220, textAlign: "center" }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {userEmail}
                </Typography>
                <Typography variant="h6" sx={{ mt: 1 }}>
                  {t("menuHiUser", { name: userName })}
                </Typography>
              </Box>
              <Divider />
              <MenuItem onClick={handleManageAccount}>
                {t("menuManageAccount")}
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleChangePassword}>
                {t("menuChangePassword")}
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>
                {t("btnLogout")}
              </MenuItem>
            </Menu>
          </>
        ) : null}
      </Box>
      <NotificationBar />
      <Routes>
        <Route path="/account-deleted" element={<AccountDeleted />} />
        <Route element={<DefaultLayout />}>
        {/* Provide users a link to your public privacy policy */}
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        {/* Provide users a link to your public terms of service */}
        <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/driver-login" element={<DriverLogin />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          {/* query-order： admin查询专用，普通用户不能访问 */}
          <Route path="/query-order" element={<CurrentOrder />} />
        </Route>
        <Route element={<ProtectedLayout />}>
          <Route path="/" element={<SelectionPage />} />
          <Route path="/history" element={<CustomerOrdersView />} />
          <Route path="/curorder" element={<CurrentOrder />} />
          <Route path="/orders" element={<CustomerOrderList />} />
          <Route path="/orderdoor" element={<OrdersPage />} />
          <Route path="/select" element={<SelectionPage />} />
          <Route path="/myinfo" element={<MyInfo />} />
          <Route path="/change-password" element={<ChangePassword />} />
          <Route path="/pickup" element={<Pickup />} />
          <Route path="/dropoff" element={<DropOff />} />
          <Route path="/success" element={<OrderSuccessCard />} />
          <Route path="/order-search" element={<OrderSearch />} />
          <Route path="/editpickup" element={<EditPickup />} />
          <Route path="/editdropoff" element={<EditDropoff />} />
        </Route>
        <Route element={<ProtectedDriverLayout />}>
          <Route path="/driver-orders" element={<DriverOrdersView />} />
          <Route path="/driver-history" element={<DriverHistoryView />} />
          <Route path="/driver-select" element={<DriverSelectionPage />} />
          <Route path="/driver-info" element={<DriverInfo />} />
          <Route path="/driver-password" element={<DriverChangePassword />} />
        </Route>
      </Routes>
      </I18nextProvider>
    </>
  );
}

export default App;
